import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { HomePage } from './pages/home';
import { LoginPage } from './pages/login';
import { NotFoundPage } from './pages/not-found';
import { ErrorPage } from './pages/error';
import { LeadsPage } from './pages/leads';
import { LeadDetailPage } from './pages/leads/detail';
import { AccountsPage } from './pages/accounts';
import { AccountDetailPage } from './pages/accounts/detail';
import { ContactsPage } from './pages/contacts';
import { ContactDetailPage } from './pages/contacts/detail';
import { OpportunitiesPage } from './pages/opportunities';
import { OpportunityDetailPage } from './pages/opportunities/detail';
import { SettingsPage } from './pages/settings';
import { AdminDashboardPage } from './pages/admin/dashboard';
import { AdminTenantsPage } from './pages/admin/tenants';
import { AdminTenantUsersPage } from './pages/admin/tenants/users';
import { AdminTenantOnboardingPage } from './pages/admin/tenants/onboarding';
import { AdminTenantOnboardingStatusPage } from './pages/admin/tenants/onboarding-status';
import { useAuthStore } from '@/stores/auth';
import { useTenantStore } from '@/stores/tenant';

type RouteGuardOptions = {
  requireAuth?: boolean;
  requireAdmin?: boolean;
  requireTenantAssignment?: boolean;
};

const withRouteGuards = (guard: RouteGuardOptions = {}) => ({
  beforeLoad: () => {
    const { isAuthenticated, user } = useAuthStore.getState();
    const { activeTenantId } = useTenantStore.getState();

    if (guard.requireAuth && !isAuthenticated) {
      throw redirect({ to: '/login' });
    }

    if (guard.requireAdmin) {
      const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
      if (!isAdmin) {
        throw redirect({ to: '/' });
      }
    }

    if (guard.requireTenantAssignment) {
      const isStaff = user?.role === 'manager' || user?.role === 'user';
      if (isStaff && !activeTenantId) {
        throw redirect({ to: '/' });
      }
    }
  },
});

// Root layout component
// eslint-disable-next-line react-refresh/only-export-components
function RootLayout() {
  return (
    <div className="min-h-screen bg-background">
      <Outlet />
    </div>
  );
}

// Create root route
const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
  errorComponent: ErrorPage,
});

// Create index route
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomePage,
  ...withRouteGuards({ requireAuth: true }),
});

// Create login route
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState();
    if (isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
});

// Search params type for list pages
type ListSearchParams = {
  create?: string;
};

// Leads routes
const leadsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leads',
  component: LeadsPage,
  ...withRouteGuards({ requireAuth: true }),
  validateSearch: (search: Record<string, unknown>): ListSearchParams => ({
    create: search.create as string | undefined,
  }),
});

const leadDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leads/$id',
  component: LeadDetailPage,
  ...withRouteGuards({ requireAuth: true }),
});

// Accounts routes
const accountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accounts',
  component: AccountsPage,
  ...withRouteGuards({ requireAuth: true }),
  validateSearch: (search: Record<string, unknown>): ListSearchParams => ({
    create: search.create as string | undefined,
  }),
});

const accountDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accounts/$id',
  component: AccountDetailPage,
  ...withRouteGuards({ requireAuth: true }),
});

// Contacts routes
const contactsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contacts',
  component: ContactsPage,
  ...withRouteGuards({ requireAuth: true }),
  validateSearch: (search: Record<string, unknown>): ListSearchParams => ({
    create: search.create as string | undefined,
  }),
});

const contactDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contacts/$id',
  component: ContactDetailPage,
  ...withRouteGuards({ requireAuth: true }),
});

// Opportunities routes
const opportunitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/opportunities',
  component: OpportunitiesPage,
  ...withRouteGuards({ requireAuth: true }),
  validateSearch: (search: Record<string, unknown>): ListSearchParams => ({
    create: search.create as string | undefined,
  }),
});

const opportunityDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/opportunities/$id',
  component: OpportunityDetailPage,
  ...withRouteGuards({ requireAuth: true }),
});

// Settings route
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage,
  ...withRouteGuards({ requireAuth: true }),
});

const adminDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/dashboard',
  component: AdminDashboardPage,
  ...withRouteGuards({ requireAuth: true, requireAdmin: true }),
});

const adminTenantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/tenants',
  component: AdminTenantsPage,
  ...withRouteGuards({ requireAuth: true, requireAdmin: true }),
});

const adminTenantUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/tenants/users',
  component: AdminTenantUsersPage,
  ...withRouteGuards({ requireAuth: true, requireAdmin: true }),
});

const adminTenantOnboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/tenants/onboarding',
  component: AdminTenantOnboardingPage,
  ...withRouteGuards({ requireAuth: true, requireAdmin: true }),
});

const adminTenantOnboardingStatusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/tenants/onboarding/$tenantId/status',
  component: AdminTenantOnboardingStatusPage,
  ...withRouteGuards({ requireAuth: true, requireAdmin: true }),
});

// Create route tree
const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  leadsRoute,
  leadDetailRoute,
  accountsRoute,
  accountDetailRoute,
  contactsRoute,
  contactDetailRoute,
  opportunitiesRoute,
  opportunityDetailRoute,
  settingsRoute,
  adminDashboardRoute,
  adminTenantsRoute,
  adminTenantUsersRoute,
  adminTenantOnboardingRoute,
  adminTenantOnboardingStatusRoute,
]);

// Create and export router
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

// Type declaration for router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
